import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { iif, Observable, of, Subscription, timer } from 'rxjs';
import { catchError, map, retry, switchMap, tap } from 'rxjs/operators';
import { selectModelPortfolioPositionDeletedSuccess, selectLoadingModelPortfolio, getSelectedModelportfolio, selectAllModelPortfolios, getLastSavedModelPortfolio, selectedIsModelPortfolioUpdated, selectIsModelPortfolioInitialState, getLastSubmittedModelPortfolioId, selectFailedTempPlannedPortfolios, selectselectedTempPlannedPortfolio, selectRebalancedPlannedPortfolios } from './../selectors/model-portfolios.selectors';
import { IModelPortfolio, IMPExportPayload } from 'src/app/models/modelPortfolios';
import { IModelPortfoliosState } from '../../state/model-portfolios.state';
import {
  SelectModelPortfolioPosition,
  SubmitModelPortfolio,
  DeleteModelPortfolio,
  DeleteModelPortfolioPositionReset,
  ResetSelectedModelPortfolio,
  SelectModelPortfolio
} from '../actions/model-portfolios.actions';
import { IModelPortfolioPosition } from 'src/app/models/modelPortfolios';
import { IAppState } from '../../state/app.state';
import {
  getModelPortfoliosState, selectModelPortfolioSubmitState, selectModelPortfolioName, selectIsLoading, selectModelPortfolioSubmittedSuccess,
  selectModelPortfolioDeletedSuccess, selectModelPortfolioOrders, selectSubscriberCashSummaries
} from '../selectors/model-portfolios.selectors';
import { ModelPortfoliosService } from "../../../services/http/model-portfolios.service";
import { MatSnackBar, MatSnackBarRef } from "@angular/material/snack-bar";
import { ProgressBarComponent } from "../../../components/shared/progressbar/progress-bar.component";

@Injectable()
export class ModelPortfoliosFacade {

    modelPortfolioStateSubscription: Subscription;
    modelPortfoliosState: IModelPortfoliosState;
    selectedModelPortfolio: IModelPortfolio;

    constructor(
        private store: Store<IAppState>,
        private modelPortfolioService: ModelPortfoliosService,
        private snackBar: MatSnackBar
    ) {
        this.modelPortfolioStateSubscription = this.modelPortfolioState$.subscribe((state: IModelPortfoliosState) => {
            this.modelPortfoliosState = state;
        });
    }

    public modelPortfolioState$ = this.store.pipe(
        select(getModelPortfoliosState)
    );

    public modelPortfolios$ = this.store.pipe(
        select(selectAllModelPortfolios)
    );

    public selectedModelPortfolio$ = this.store.pipe(
        select(getSelectedModelportfolio)
    ).pipe(map((selectedMP: IModelPortfolio) => {
        if (selectedMP && selectedMP.positions && selectedMP.subscriptions) {
            const mp = {
                ...selectedMP,
                positions: [
                    ...selectedMP.positions
                ],
                subscriptions: [
                    ...selectedMP.subscriptions
                ]
            };
            return mp;
        }
    }));

    public failedTempPlannedPortfolios$ = this.store.pipe(
        select(selectFailedTempPlannedPortfolios)
    );
    public selectRebalancedPlannedPortfolios$ = this.store.pipe(
        select(selectRebalancedPlannedPortfolios)
    );

    public selectedTempPlannedPortfolio$ = this.store.pipe(
        select(selectselectedTempPlannedPortfolio)
    );

    public lastSavedModelPortfolio$ = this.store.pipe(
        select(getLastSavedModelPortfolio)
    );
    public lastSubmittedModelPortfolioId$ = this.store.pipe(
        select(getLastSubmittedModelPortfolioId)
    );

    public modelPortfolioUpdated$ = this.store.pipe(
        select(selectedIsModelPortfolioUpdated)
    );
    public selectedModelPortfolioName$ = this.store.pipe(
        select(selectModelPortfolioName)
    );
    public loading$ = this.store.pipe(
        select(selectIsLoading)
    );
    public modelPortfolioSubmittedSuccess$ = this.store.pipe(
        select(selectModelPortfolioSubmittedSuccess)
    );
    public modelPortfolioDeletedSuccess$ = this.store.pipe(
        select(selectModelPortfolioDeletedSuccess)
    );
    public modelPortfolioPositionDeletedSuccess$ = this.store.pipe(
        select(selectModelPortfolioPositionDeletedSuccess)
    );
    public isMPUpdatedSuccessful$ = this.store.pipe(
        select(selectModelPortfolioSubmitState)
    ).pipe(map((submitState: {
        modelPortfolioSubmittedSuccess: boolean,
        loading: boolean
    }) => {
        if (submitState.modelPortfolioSubmittedSuccess && !submitState.loading) {
            return true;
        } else {
            return false;
        }
    }));

    public isModelPortfolioInitialState$ = this.store.pipe(
        select(selectIsModelPortfolioInitialState)
    );

    public selectLoadingModelPortfolio$ = this.store.pipe(
        select(selectLoadingModelPortfolio)
    );

    public selectModelPortfolioSubmitState$ = this.store.pipe(
        select(selectModelPortfolioSubmitState)
    );

    public modelPortfolioOrders$ = this.store.pipe(
        select(selectModelPortfolioOrders)
    );

    public subscriberCashSummaries$ = this.store.pipe(
        select(selectSubscriberCashSummaries)
    );

    selectModelPortfolioPosition(position: IModelPortfolioPosition) {
        this.store.dispatch(new SelectModelPortfolioPosition(position));
    }

    submitModelPortfolio(modelPortfolio: IModelPortfolio) {
        this.store.dispatch(new SubmitModelPortfolio(modelPortfolio));
    }

    deleteModelPortfolio(modelPortfolioPayload: {
        modelPortfolioId: number,
        modelPortfolioName: string
    }) {
        this.store.dispatch(new DeleteModelPortfolio(modelPortfolioPayload));
    }

    deleteModelPortfolioPositionReset() {
        this.store.dispatch(new DeleteModelPortfolioPositionReset());
    }

    resetSelectedModelPortfolio(lastSavedModelPortfolio: IModelPortfolio) {
        this.store.dispatch(new ResetSelectedModelPortfolio(lastSavedModelPortfolio));
    }

    selectModelPortfolio(model: IModelPortfolio) {
        this.store.dispatch(new SelectModelPortfolio(model));
    }

    // API call to fetch model portfolios for given portfolioIds
    getModelPortfolios(portfolioIds: number[]) {
        return this.modelPortfolioService.getModelPortfoliosForPortfolios(portfolioIds)
            // Retry up to 3 times on error.
            .pipe(retry(3));
    }

    /**
     * Polls the model portfolio drift calculation count and shows progress using a snack bar.
     * If there are no portfolios remaining, it dismisses the snack bar and returns `true`.
     * If there are portfolios remaining, it updates the snack bar with the count and continues polling every second until the count reaches zero.
     *
     * @return {Observable<boolean>} An observable that emits `true` if polling completes successfully,
     *                               or `false` in case of an error.
     */
    tryPollModelPortfolioDriftCalculationCount$(numberOfPortfolioToCalculate: number, snackBarRef: MatSnackBarRef<ProgressBarComponent> = null): Observable<boolean> {
        let pollingRateMilliseconds = 3000;

        return this.modelPortfolioService.pollCalculateModelPortfolioDriftCount()
            .pipe(
                // Before polling, make sure the progressbar is open
                tap(portfoliosRemaining => {

                    // Just return if we've already initialised the progressbar.
                    if (snackBarRef)
                        return;

                    snackBarRef = this.snackBar.openFromComponent(
                            ProgressBarComponent, {
                                horizontalPosition: 'right',
                                verticalPosition: 'bottom',
                                data: {
                                    title: 'Calculating model portfolio drift',
                                    message: `${ portfoliosRemaining } portfolios remaining. Please wait.`,
                                }
                            }
                        );
                    }
                ),
                switchMap(
                    portfoliosRemaining =>
                        iif(() => (portfoliosRemaining > 0),
                            // If there's still portfolios remaining, re-poll after the timer finishes
                            timer(pollingRateMilliseconds).pipe(
                                switchMap(() => {
                                    // Update the progressbar data
                                    snackBarRef.instance.data.message = `${ portfoliosRemaining } portfolios remaining. Please wait.`;
                                    snackBarRef.instance.data.progress = ((numberOfPortfolioToCalculate - portfoliosRemaining) / numberOfPortfolioToCalculate) * 100;

                                    // Re-poll via recursion
                                    return this.tryPollModelPortfolioDriftCalculationCount$(numberOfPortfolioToCalculate, snackBarRef);
                                })
                            ),
                            // If no more portfolios, return true to let the caller know we're done.
                            of(true)
                        )
                ),
                catchError(error => {
                    console.error(error);
                    return of(false);
                })
            );
    }
}
